import type { LiveMatchEventType, Player } from './types';

export type VoiceCommandResult = {
  ok: boolean;
  player?: Player;
  eventType?: LiveMatchEventType;
  transcript: string;
  confidence: 'high' | 'medium' | 'low';
  message: string;
};

const EVENT_PATTERNS: Array<[LiveMatchEventType, RegExp[]]> = [
  ['shot_on_target', [/\bshot on target\b/i, /\bshot saved\b/i, /\bstrza[lł] celny\b/i, /\bcelny strza[lł]\b/i]],
  ['shot', [/\bshot\b/i, /\bstrza[lł]\b/i]],
  ['goal', [/\bgoal\b/i, /\bscored\b/i, /\bgol\b/i, /\bbramka\b/i]],
  ['assist', [/\bassist\b/i, /\basysta\b/i]],
  ['key_pass', [/\bkey pass\b/i, /\bkluczowe podanie\b/i]],
  ['touch', [/\btouch\b/i, /\bball touch\b/i, /\bkontakt z pi[lł]k[aą]\b/i, /\bdotkni[eę]cie\b/i]],
  ['duel_won', [/\bduel won\b/i, /\bwon duel\b/i, /\bwygrany pojedynek\b/i]],
  ['duel_lost', [/\bduel lost\b/i, /\blost duel\b/i, /\bprzegrany pojedynek\b/i]],
  ['tackle_won', [/\btackle won\b/i, /\bwon tackle\b/i, /\bwygrany odbi[oó]r\b/i, /\budany odbi[oó]r\b/i]],
  ['tackle_lost', [/\btackle lost\b/i, /\bprzegrany odbi[oó]r\b/i]],
  ['interception', [/\binterception\b/i, /\bprzechwyt\b/i]],
  ['clearance', [/\bclearance\b/i, /\bwybicie\b/i]],
  ['header_won', [/\bheader won\b/i, /\bwon header\b/i, /\bwygrana g[lł][oó]wka\b/i]],
  ['header_lost', [/\bheader lost\b/i, /\bprzegrana g[lł][oó]wka\b/i]],
  ['foul_won', [/\bfoul won\b/i, /\bwon foul\b/i, /\bfaul na nim\b/i]],
  ['foul_committed', [/\bfoul committed\b/i, /\bcommitted foul\b/i, /\bfaul\b/i]],
  ['offside', [/\boffside\b/i, /\bspalony\b/i]],
  ['save', [/\bsave\b/i, /\bsaved\b/i, /\bobrona\b/i, /\binterwencja\b/i]],
  ['ball_lost', [/\bball lost\b/i, /\blost ball\b/i, /\bstrata\b/i, /\bstrata pi[lł]ki\b/i]],
  ['yellow', [/\byellow\b/i, /\byellow card\b/i, /\b[zż][oó][lł]ta kartka\b/i]],
  ['red', [/\bred card\b/i, /\bczerwona kartka\b/i]],
  ['injury', [/\binjury\b/i, /\bkontuzja\b/i]],
];

function normalise(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[.,;:!?]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function playerAliases(player: Player) {
  const parts = normalise(player.name).split(' ').filter(Boolean);
  const aliases = new Set<string>([
    normalise(player.name),
    parts[0] || '',
    parts[parts.length - 1] || '',
  ]);
  if (player.squad_number !== null && player.squad_number !== undefined) {
    aliases.add(String(player.squad_number));
    aliases.add(`#${player.squad_number}`);
    aliases.add(`number ${player.squad_number}`);
    aliases.add(`numer ${player.squad_number}`);
  }
  const bio = player.bio || {};
  const nickname = typeof bio.nickname === 'string' ? bio.nickname : '';
  if (nickname) aliases.add(normalise(nickname));
  return [...aliases].filter((alias) => alias.length >= 1);
}

export function parseVoiceCommand(transcript: string, players: Player[]): VoiceCommandResult {
  const raw = transcript.trim();
  const text = normalise(raw);
  if (!text) return { ok: false, transcript: raw, confidence: 'low', message: 'No command heard.' };

  let matchedPlayer: Player | undefined;
  let matchedAlias = '';
  for (const player of players) {
    for (const alias of playerAliases(player)) {
      const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`(?:^|\\s)#?${escaped}(?:\\s|$)`, 'i').test(text) && alias.length >= matchedAlias.length) {
        matchedPlayer = player;
        matchedAlias = alias;
      }
    }
  }

  let eventType: LiveMatchEventType | undefined;
  for (const [type, patterns] of EVENT_PATTERNS) {
    if (patterns.some((pattern) => pattern.test(text))) {
      eventType = type;
      break;
    }
  }

  if (!matchedPlayer && !eventType) {
    return { ok: false, transcript: raw, confidence: 'low', message: 'Player and action not recognised.' };
  }
  if (!matchedPlayer) {
    return { ok: false, transcript: raw, eventType, confidence: 'medium', message: 'Action recognised. Player missing.' };
  }
  if (!eventType) {
    return { ok: false, transcript: raw, player: matchedPlayer, confidence: 'medium', message: 'Player recognised. Action missing.' };
  }

  return {
    ok: true,
    transcript: raw,
    player: matchedPlayer,
    eventType,
    confidence: matchedAlias === String(matchedPlayer.squad_number ?? '') || matchedAlias === normalise(matchedPlayer.name) ? 'high' : 'medium',
    message: `${matchedPlayer.name} · ${eventType.replace(/_/g, ' ')}`,
  };
}
